import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {useField, useFormikContext} from "formik";
import {debounce, throttle} from "lodash";
import PropTypes from "prop-types";

import {detectTextLanguage} from "../../utilities/generalConstants.js/generalConstants";
import {toaster} from "../../utilities/hooks/toaster";
import {KEYBOARD_CODES} from "../../utilities/keyboardCodes";
import QuestionItem from "../../utilities/QuestionItem";
import ToolTip from "../../utilities/ToolTip";
import useRoutePermissions, {
  UI_PERMISSION_TYPE,
} from "../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";

import useInputsTooltip from "./hook/InputsTooltipService";

export const Select = ({
  label,
  allowNullValue = false, //дозвіл на використання значення null
  width = "100%",
  // styles = null,
  id,
  labelWrap,
  options, ///// должен быть вида [{value: ###, title: ### }, ....]
  makeEffect = null, /// функция (не обязательная) которая должна сработать когда происходит выбор из выпадающего списка
  question, ///// пояснение к полю
  // nomination = "",
  // multi = false, ///если передаём в массив выбора объекты с несколькими dependentField например {orgId, orgName, orgCode}
  minFilterLength = 3, //// минимальная длина строки для фильтрации поля
  errorField,
  labelwidth = "",
  disabled = false,
  selectOnly = false,
  inputClassName,
  generalClassName = "",
  labelClassName = "",
  optionsAbove = false,
  optionsLeft = false,
  optionsListStyle = {},
  style,
  textMode = false,
  required,
  onBlur = () => {},
  placeholder = "",
  tooltipMessage,
  bottomTooltip = false,
  grow = false,
  noFunctional = false,
  noInput = false, //without input field
  isRequiredAccented = true,
  isLayoutWarningShown = false,
  hidden = false,
  uiPermissionType = UI_PERMISSION_TYPE.WRITABLE,
  ...props
}) => {
  const {
    t,
    i18n: {language},
  } = useTranslation();
  const {initialValues, errors, submitCount, setValues} = useFormikContext();
  const [field, meta, helpers] = useField(props);
  const {value} = field;

  const {isDisabledByRoutePermission} = useRoutePermissions({uiPermissionType});

  const optionsListRef = useRef();
  const optionsItemRef = useRef();
  const optionsListContainerRef = useRef();
  const inputRef = useRef();

  const isInputFocused = document.activeElement === inputRef.current;

  const [textValue, setTextValue] = useState(initialValues?.[props?.name] ?? "");
  const [currentOption, setCurrentOption] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [filter, setFilter] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);

  const normalizedOptions = Array.isArray(options) ? options : [];
  const optionsLength = filteredOptions?.length;

  const {tooltipShown, onMouseEnter, onMouseLeave} = useInputsTooltip(field);

  const labelText = label ? t(label) : "";
  useEffect(() => {
    if (!showOptions) {
      setFocusedOptionIndex(null);
    }
  }, [showOptions]);

  function onOptionsListKeyDown(e) {
    const currentOptionsListRef = optionsListRef.current;
    const optionHeight = optionsItemRef.current?.offsetHeight;
    const scrollBottomValue = optionHeight + 0.5692;
    const scrollTopValue = optionHeight - 0.1652;

    switch (e.code) {
      case KEYBOARD_CODES.TAB:
        if (showOptions) setShowOptions(false);
        break;
      case KEYBOARD_CODES.ENTER:
        if (focusedOptionIndex !== null) {
          handleSelectOption(filteredOptions[focusedOptionIndex]);
        }
        break;

      case KEYBOARD_CODES.ARROW_UP:
        if (showOptions) {
          if (focusedOptionIndex !== null) {
            setFocusedOptionIndex(prev => (prev !== 0 ? --prev : optionsLength - 1));
            currentOptionsListRef.scrollTop =
              focusedOptionIndex !== 0
                ? currentOptionsListRef.scrollTop - scrollTopValue
                : currentOptionsListRef.scrollHeight;
          } else {
            setFocusedOptionIndex(optionsLength - 1);
            currentOptionsListRef.scrollTop = currentOptionsListRef.scrollHeight;
          }
        } else {
          setShowOptions(true);
        }
        break;
      case KEYBOARD_CODES.ARROW_DOWN:
        if (showOptions) {
          if (focusedOptionIndex !== null && showOptions) {
            setFocusedOptionIndex(prev => (prev !== optionsLength - 1 ? ++prev : 0));
            currentOptionsListRef.scrollTop =
              focusedOptionIndex !== optionsLength - 1 ? currentOptionsListRef.scrollTop + scrollBottomValue : 0;
          } else {
            setFocusedOptionIndex(0);
            currentOptionsListRef.scrollTop = 0;
          }
        } else {
          setShowOptions(true);
        }
        break;
    }
  }

  // const fieldValue = value || initialValues[props.name] || "";

  //////////фильтрация

  const updateFilterQuery = filterStr => {
    if (filterStr && filterStr.length >= minFilterLength) {
      setFilter(filterStr);
    } else setFilter("");
  };

  const delayedFilter = debounce(updateFilterQuery, 500);
  // const namingType = useMemo(() => (i18n.language === "en" ? "engName" : "name"), [i18n.language]);

  const optionsStringified = JSON.stringify(normalizedOptions);
  const filteredOptionsStringified = JSON.stringify(filteredOptions);

  useEffect(() => {
    if (filter === "" && optionsStringified !== filteredOptionsStringified) {
      setFilteredOptions(normalizedOptions);
      return;
    }

    const filtered = normalizedOptions.filter(option => {
      if (!option?.title || option.title == "") {
        return false;
      }
      // const words = option.title.split(" ");

      return option.title.toLowerCase().includes(filter.toLowerCase());

      // return words.some(word => word.toLowerCase().includes(filter.toLowerCase()));
    });

    const debouncedSetFilteredOptions = debounce(setFilteredOptions, 250);
    debouncedSetFilteredOptions(filtered);

    if (filter !== "" && !showOptions && filtered.length > 0 && filtered.length !== normalizedOptions.length) {
      // openOptions();
    }
  }, [filter, optionsStringified, filteredOptionsStringified, showOptions]);

  /////////////////////////
  const timeout = useRef();

  const handleSelectOption = option => {
    clearTimeout(timeout.current);
    setShowOptions(false);
    setFilter("");

    helpers.setValue(textMode ? option.title : option.value);
    if (makeEffect) makeEffect(option);
    // setTextValue(option.title)
  };

  const optionList = useMemo(() => {
    return (
      Array.isArray(filteredOptions) &&
      filteredOptions?.map((option, i) => {
        var {title} = option;
        const isSelected = focusedOptionIndex !== null && filteredOptions[focusedOptionIndex]?.title === title;

        return (
          <li
            className={`select_option ${isSelected ? "selected_option" : ""} ${
              option?.disabled ? "select_li_disabled" : ""
            }`}
            key={i}
            onClick={() => {
              if (!option?.disabled) handleSelectOption(option);
            }}
            ref={optionsItemRef}
          >
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%"}}>
              <div style={{display: "flex", alignItems: "center", minWidth: 0, flex: 1, paddingRight: 8}}>
                {option?.iconSrc ? (
                  <img
                    src={option.iconSrc}
                    alt={option?.iconAlt || ""}
                    style={{
                      width: 16,
                      height: 12,
                      minWidth: 16,
                      objectFit: "cover",
                      marginRight: 8,
                      borderRadius: 2,
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                    }}
                  />
                ) : null}
                <span style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                  {title?.length > 32 ? title.slice(0, 32) + "..." : title}
                </span>
              </div>
              {option?.badge ? (
                <span
                  style={{
                    marginLeft: 8,
                    padding: "2px 6px",
                    border: "0.5px solid",
                    background: "rgb(255, 242, 231)",
                    // color: "#fff",
                    borderRadius: 4,
                    fontSize: 11,
                    // lineHeight: 1.2,
                    // textTransform: "uppercase",
                  }}
                >
                  {option.badge}
                </span>
              ) : null}
            </div>
          </li>
        );
      })
    );
  }, [filteredOptions?.length, focusedOptionIndex, filteredOptions]);

  useEffect(() => {
    setShowOptions(false);
    setFilter("");
    // if (value && value !== "" && (options.length === 0 || options.find(opt => opt.value === value)?.length === 0)) {
    //   console.log("remove", value);
    //   setCurrentOption(null);
    //   setTextValue("");
    //   helpers.setValue(null);
    //   return;
    // }
    valueChange(value);
  }, [optionsStringified]);

  const openOptions = () => {
    if (filteredOptions.length > 0 && !props.readOnly && !isDisabledByRoutePermission) setShowOptions(true);
  };

  useEffect(() => {
    showAllOptions();
  }, [initialValues]);

  const showAllOptions = useCallback(() => {
    setFilter("");
  }, []);

  //////// findId ////////

  // const updateIdQuery = value => {
  //   if (value && value !== "") {
  //     var item = options.find(option => option.title === value);
  //     if (item) helpers.setValue(item.value);
  //     else {
  //       helpers.setValue(value);
  //       if (makeEffect) makeEffect(null);
  //     }
  //   } else {
  //     {
  //       helpers.setValue(null);
  //       if (makeEffect) makeEffect(null);
  //     }
  //   }
  // };

  const valueChange = value => {
    if (value || (allowNullValue && value === null)) {
      if (value?.title) {
        setTextValue(value.title);
        if (textMode) helpers.setValue(normalizedOptions.find(option => option.value === value))?.title ?? "";
      } else if (currentOption?.value === value) {
        return;
      } else if (normalizedOptions.length > 0) {
        const option = normalizedOptions.find(option => option?.value === value);
        if (option) {
          setTextValue(option.title);
          setCurrentOption(option);
        }
      } else if (normalizedOptions.length < 1) {
        setTextValue("");
        return;
      }
    }

    // else if ((value === "" || !value) && currentOption && currentOption?.value !== value) {
    //   setCurrentOption(null);
    //   setTextValue("");
    //   // if (textMode) setValues(prev => ({...prev, [props.name]: ""}));
    // } else if (!value && !showOptions) {
    //   // setTimeout(() => setTextValue(""));
    //   // setTextValue("");
    // }
  };

  useEffect(() => {
    valueChange(value);
    if (textMode) setTextValue(value);
    if (!value && (value !== null || !allowNullValue)) {
      // setTimeout(() => setTextValue(""));
      // setTextValue("");
      // setFilter("");
      setCurrentOption(null);

      //очищать текст инпутов не в фокусе при пустом value формика
      if (!showOptions && !currentOption?.value && !isInputFocused) {
        setTextValue("");
      }
      // if (!isInputFocused && showOptions) setShowOptions(false);
    }

    // if (!isInputFocused) setShowOptions(false);
  }, [value, currentOption?.value, showOptions, isInputFocused]);

  useEffect(() => {
    if (textMode) {
      setValues(prev => ({...prev, [props.name]: textValue}));
    }
  }, [textValue, textMode]);

  useEffect(() => {
    if ((currentOption?.value || (allowNullValue && currentOption?.value === null)) && normalizedOptions.length > 0) {
      const title = normalizedOptions.find(option => option?.value === currentOption?.value)?.title;
      setTextValue(title || "");
      if (textMode) helpers.setValue(title ?? "");
    }
    // else {
    //   setTextValue("");
    // }
  }, [optionsStringified, currentOption?.value]);

  // useEffect(() => {
  //   console.log("uef6");
  //   updateIdQuery(textValue);
  // }, [textValue]);

  const showLayoutWarning = useMemo(
    () =>
      throttle(() => {
        toaster.warning("Caps/keyboard layout may be wrong");
      }, 3000),
    [],
  );

  const onInputChange = e => {
    const currentText = e.target.value;

    if (
      isLayoutWarningShown &&
      typeof currentText === "string" &&
      currentText?.trim() &&
      detectTextLanguage(currentText) !== language
    ) {
      showLayoutWarning();
    }

    setTextValue(currentText);
    delayedFilter(e.target.value);
    openOptions();

    if (currentText && currentText !== "") {
      const option = normalizedOptions.find(option => option?.title === currentText);
      if (option) {
        helpers.setValue(textMode ? currentText : option.value);
        setCurrentOption(option);
      } else if (value !== null || value !== "") {
        helpers.setValue(null);
        if (makeEffect) makeEffect(null);
        setCurrentOption(null);
      }
    } else if (value !== null || value !== "") {
      {
        helpers.setValue(null);
        if (makeEffect) makeEffect(null);
        setCurrentOption(null);
      }
    }
  };

  const onMouseLeaveEvent = () => {
    if (!currentOption && !value) {
      setFilter("");
      setTextValue("");
    }
    setTimeout(() => showOptions && setShowOptions(false), 100);
  };

  // const findId = debounce(updateIdQuery, 500);
  const isRequiredMarkHighlighted =
    !field.value && !disabled && !noFunctional && !props.readOnly && !isDisabledByRoutePermission;

  return (
    <ToolTip
      title={
        noFunctional
          ? "No functional"
          : isDisabledByRoutePermission && !props.readOnly && !disabled
          ? "Your position has no permission for this action"
          : tooltipMessage
      }
      bottom={bottomTooltip}
      className={clsx(generalClassName, grow && "fGrow", hidden && "hiddenEl")}
    >
      <div className={`project_input select  ${labelWrap ? "" : "labelWrap"}`} id={id} style={style}>
        <>
          {label && (
            <label
              htmlFor={props.name}
              style={{width: labelwidth, whiteSpace: "pre"}}
              className={`${props.autolabel ? "autolabel" : ""} ${labelClassName}`}
            >
              {/* {  typeof label === "string" ? t(label) : label  } */}

              <span>
                {labelText}
                {required && (
                  <span className={isRequiredAccented && isRequiredMarkHighlighted ? "required-asterisk" : ""}>*</span>
                )}
                :
              </span>
            </label>
          )}
          <div
            style={{
              display: "flex",
              position: "relative",
              width: width,
              maxHeight: "100px",
              ...(grow ? {flex: "1 1 auto", minWidth: 0} : {}),
            }}
            onMouseLeave={onMouseLeaveEvent}
          >
            {currentOption?.iconSrc ? (
              <img
                src={currentOption.iconSrc}
                alt={currentOption?.iconAlt || ""}
                style={{
                  position: "absolute",
                  left: 8,
                  width: 16,
                  height: 12,
                  objectFit: "cover",
                  borderRadius: 2,
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            ) : null}
            {!noInput && (
              <input
                id={props.name}
                className={inputClassName}
                readOnly={props.readOnly || selectOnly}
                value={textValue}
                autoComplete="off"
                style={{
                  width: "100%",
                  ...(currentOption?.iconSrc ? {paddingLeft: 30} : {}),
                  ...(props.readOnly || disabled || noFunctional || isDisabledByRoutePermission
                    ? {cursor: "default"}
                    : {background: "white"}),
                }}
                onClick={openOptions}
                onChange={e => {
                  // setTextValue(e.target.value);
                  onInputChange(e);
                  // findId(e.target.value);
                  // delayedFilter(e.target.value);
                  // makeEffect(e.target.value);
                }}
                onBlur={e => onBlur({e, setTextValue})}
                disabled={isDisabledByRoutePermission || disabled || noFunctional}
                onKeyDown={onOptionsListKeyDown}
                onMouseEnter={question ? onMouseEnter : null}
                onMouseLeave={question ? onMouseLeave : null}
                placeholder={t(placeholder)}
                ref={inputRef}
              />
            )}

            {meta.error && (meta.touched || submitCount > 0) && !isDisabledByRoutePermission && !props.readOnly ? (
              <div className="error">{meta.error}</div>
            ) : null}
            {errors[errorField] && meta.touched && submitCount > 0 ? (
              <div className="error">{errors[errorField]}</div>
            ) : null}
            {!props.readOnly && !isDisabledByRoutePermission && (
              <div
                className={`select__list_button select__list_button-responsive ${
                  disabled || noFunctional ? "disable" : ""
                }`}
                onClick={() => {
                  if (disabled || noFunctional) return;
                  if (!props.readOnly) {
                    showAllOptions();
                    setShowOptions(i => (i = !i));
                  }
                }}
              >
                ▼
              </div>
            )}

            {showOptions && (
              <div
                className={`select__list disable ${optionsAbove ? "select__list--above" : ""} ${
                  optionsLeft ? "select__list--left" : ""
                }`}
                ref={optionsListContainerRef}
                onKeyDown={onOptionsListKeyDown}
                tabIndex="0"
                style={{...optionsListStyle, height: "unset"}}
                // onMouseLeave={() => setTimeout(() => showOptions && setShowOptions(false), 100)}
              >
                <div className="optionListHeader">
                  {optionList.length ? t("Choose an option:") : t("No such option found")}
                </div>
                <ul
                  ref={optionsListRef}
                  className="fcCol"
                  style={{
                    alignItems: "stretch",
                    ...(optionsListStyle?.height ? {maxHeight: optionsListStyle.height} : {}),
                  }}
                >
                  {optionList}
                </ul>
              </div>
            )}
          </div>
          {question ? <QuestionItem style={{paddingLeft: "1px"}} title={question} tooltipShown={tooltipShown} /> : null}
        </>
      </div>
    </ToolTip>
  );
};

Select.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.node]),
  width: PropTypes.string,
  question: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  props: PropTypes.array,
  id: PropTypes.string,
  options: PropTypes.array,
  minFilterLength: PropTypes.number,
  errorField: PropTypes.string,
  disabled: PropTypes.bool,
  labelClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  optionsAbove: PropTypes.bool,
  textMode: PropTypes.bool,
  placeholder: PropTypes.string,
  grow: PropTypes.bool,
  noFunctional: PropTypes.bool,
  isLayoutWarningShown: PropTypes.bool,
  hidden: PropTypes.bool,
};
